import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import SideBar from "../components/SideBar";
import itPortalService from "../services/itPortalService";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import {
  MdEdit,
  MdDelete,
  MdRefresh,
  MdOutlineAccountBalanceWallet,
  MdOutlineAdminPanelSettings,
  MdOutlineReceiptLong,
} from "react-icons/md";
import "../styles/ManageMember.css";
import "../styles/ManageUserTopCoin.css";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

export default function ManageUserTopCoin() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  // Notification state
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Modal states
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    UserType: "",
    TopCoinValue: 1,
    CashValue: 0.005,
    MinimumTopCoinToClaim: 10000,
    Notes: "",
  });

  useEffect(() => {
    fetchRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showSnack = (message, severity = "success") => {
    setSnack({ open: true, message, severity });
  };

  const handleSnackClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnack((prev) => ({ ...prev, open: false }));
  };

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const response = await itPortalService.getUserTopCoins();
      const list = Array.isArray(response?.data?.data)
        ? response.data.data
        : Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
        ? response
        : [];
      setRecords(list);
    } catch (err) {
      console.error("Failed to load User TopCoin configurations:", err);
      if (err.response?.status === 403 && err.response?.data?.requires_it_portal_otp) {
        navigate("/it-portal");
      } else {
        showSnack(
          err.response?.data?.message || err.message || "Failed to load User TopCoin configurations.",
          "error"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  // Open Edit Modal
  const handleOpenEdit = (record) => {
    setSelectedRecord(record);
    setFormData({
      UserType: record.UserType,
      TopCoinValue: record.TopCoinValue ?? 1,
      CashValue: record.CashValue ?? 0.005,
      MinimumTopCoinToClaim: record.MinimumTopCoinToClaim ?? "",
      Notes: record.Notes || "",
    });
    setIsEditModalOpen(true);
  };

  // Open Add Modal
  const handleOpenAdd = () => {
    setFormData({
      UserType: "",
      TopCoinValue: 1,
      CashValue: 0.005,
      MinimumTopCoinToClaim: 10000,
      Notes: "",
    });
    setIsAddModalOpen(true);
  };

  // Open Delete Modal
  const handleOpenDelete = (record) => {
    setSelectedRecord(record);
    setIsDeleteModalOpen(true);
  };

  // Save Edit
  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!selectedRecord) return;

    setIsSaving(true);
    try {
      await itPortalService.updateUserTopCoin(selectedRecord.ID, {
        UserType: formData.UserType,
        TopCoinValue: parseInt(formData.TopCoinValue, 10),
        CashValue: parseFloat(formData.CashValue),
        MinimumTopCoinToClaim:
          formData.MinimumTopCoinToClaim !== ""
            ? parseInt(formData.MinimumTopCoinToClaim, 10)
            : null,
        Notes: formData.Notes || null,
      });

      showSnack(`"${formData.UserType}" configuration updated successfully!`);
      setIsEditModalOpen(false);
      fetchRecords();
    } catch (err) {
      showSnack(
        err.response?.data?.message ||
          (err.response?.data?.errors
            ? Object.values(err.response.data.errors).flat().join(" ")
            : "Failed to update configuration."),
        "error"
      );
    } finally {
      setIsSaving(false);
    }
  };

  // Save Add
  const handleSaveAdd = async (e) => {
    e.preventDefault();
    if (!formData.UserType.trim()) {
      showSnack("User Type name is required.", "error");
      return;
    }

    setIsSaving(true);
    try {
      await itPortalService.createUserTopCoin({
        UserType: formData.UserType.trim(),
        TopCoinValue: parseInt(formData.TopCoinValue, 10) || 1,
        CashValue: parseFloat(formData.CashValue) || 0.005,
        MinimumTopCoinToClaim:
          formData.MinimumTopCoinToClaim !== ""
            ? parseInt(formData.MinimumTopCoinToClaim, 10)
            : null,
        Notes: formData.Notes || null,
      });

      showSnack(`Configuration for "${formData.UserType}" created successfully!`);
      setIsAddModalOpen(false);
      fetchRecords();
    } catch (err) {
      showSnack(
        err.response?.data?.message ||
          (err.response?.data?.errors
            ? Object.values(err.response.data.errors).flat().join(" ")
            : "Failed to create configuration."),
        "error"
      );
    } finally {
      setIsSaving(false);
    }
  };

  // Confirm Delete
  const handleConfirmDelete = async () => {
    if (!selectedRecord) return;

    setIsSaving(true);
    try {
      await itPortalService.deleteUserTopCoin(selectedRecord.ID);
      showSnack(`"${selectedRecord.UserType}" configuration removed successfully.`);
      setIsDeleteModalOpen(false);
      fetchRecords();
    } catch (err) {
      showSnack(
        err.response?.data?.message || "Failed to delete configuration.",
        "error"
      );
    } finally {
      setIsSaving(false);
    }
  };

  // Filtered records
  const filteredRecords = records.filter((r) =>
    r.UserType?.toLowerCase().includes(search.toLowerCase())
  );

  // Badge class helper
  const getUserTypeBadgeClass = (userType) => {
    const lower = (userType || "").toLowerCase();
    if (lower.includes("customer") && !lower.includes("agent")) return "it-badge-customer";
    if (lower.includes("retail")) return "it-badge-retail";
    if (lower.includes("mini")) return "it-badge-mini-agent";
    if (lower.includes("senior")) return "it-badge-senior-agent";
    if (lower.includes("member")) return "it-badge-member";
    return "it-badge-default";
  };

  return (
    <div className="manage-member-layout">
      <SideBar />

      <div className="main-content">
        {/* Header Bar matching SuperAdmin style */}
        <div className="header-bar" style={{ border: 0, boxShadow: "none" }}>
          <div className="header-top">
            <h2 className="header-title">User TopCoin Management</h2>
            <div className="it-header-actions">
              {/* <button
                className="it-btn-refresh"
                onClick={fetchRecords}
                title="Refresh table data"
              >
                <MdRefresh size={18} /> Refresh
              </button> */}
              <button className="action-btn" onClick={handleOpenAdd}>
                + Add User Type
              </button>
            </div>
          </div>

          {/* IT Portal Subnavigation Tabs */}
          {/* <div className="it-tabs-container">
            <button
              className="it-tab-item active"
              onClick={() => navigate("/it-portal/user-topcoin")}
            >
              <MdOutlineAccountBalanceWallet size={18} /> User TopCoin Management
            </button>
            <button
              className="it-tab-item"
              onClick={() => navigate("/it-portal/vat-config")}
            >
              <MdOutlineReceiptLong size={18} /> VAT Configuration
            </button>
            <button
              className="it-tab-item"
              onClick={() => navigate("/it-portal/dashboard?tab=security")}
            >
              <MdOutlineAdminPanelSettings size={18} /> Security Settings
            </button>
          </div> */}

          {/* Quick Statistics matching SuperAdmin style */}
          {/* <div className="user-statistics">
            <div className="stat-item">
              <span className="stat-label">Total User Types:</span>
              <span className="stat-value">{records.length}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Filtered Roles:</span>
              <span className="stat-value">{filteredRecords.length}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Base TopCoin Value:</span>
              <span className="stat-value">
                {records.length > 0 ? records[0].TopCoinValue : 1} TC
              </span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Cash Value Rate:</span>
              <span className="stat-value">
                £{records.length > 0 ? Number(records[0].CashValue).toFixed(4) : "0.0050"}
              </span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Customer Min Claim:</span>
              <span className="stat-value">
                {records.find((r) => r.UserType?.toLowerCase() === "customer")
                  ?.MinimumTopCoinToClaim ?? 250}{" "}
                TC
              </span>
            </div>
          </div> */}

          {/* Filter Bar matching SuperAdmin style */}
          <div className="filters">
            <div className="input-group" style={{ flexGrow: 1 }}>
              <label>Search User Type:</label>
              <input
                type="text"
                placeholder="Search by user type name..."
                className="search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                maxLength={30}
              />
            </div>
          </div>
        </div>

        {/* Table Container & Member Table matching SuperAdmin style */}
        <div className="table-container">
          <table className="member-table">
            <thead>
              <tr>
                <th style={{ width: "70px" }}>ID</th>
                <th style={{ width: "230px" }}>User Type</th>
                <th style={{ width: "160px" }}>TopCoin Value</th>
                <th style={{ width: "160px" }}>Cash Value (£)</th>
                <th style={{ width: "190px" }}>Min Claim</th>
                <th>Notes</th>
                <th style={{ width: "160px", textAlign: "center" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: "center", padding: "30px" }}>
                    Loading User TopCoin configurations...
                  </td>
                </tr>
              ) : filteredRecords.length > 0 ? (
                filteredRecords.map((item) => (
                  <tr key={item.ID}>
                    <td style={{ fontWeight: 600 }}>{item.ID}</td>
                    <td>
                      <span className={`it-badge ${getUserTypeBadgeClass(item.UserType)}`}>
                        {item.UserType}
                      </span>
                    </td>
                    <td>
                      <strong>{item.TopCoinValue}</strong> TopCoin
                    </td>
                    <td>
                      <span style={{ color: "#059669", fontWeight: 700 }}>
                        £{Number(item.CashValue).toFixed(4)}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`it-claim-pill ${
                          item.MinimumTopCoinToClaim === 250 ? "special" : ""
                        }`}
                      >
                        {item.MinimumTopCoinToClaim !== null && item.MinimumTopCoinToClaim !== undefined
                          ? Number(item.MinimumTopCoinToClaim).toLocaleString() + " TC"
                          : "—"}
                      </span>
                    </td>
                    <td style={{ color: item.Notes ? "#334155" : "#94a3b8" }}>
                      {item.Notes || "—"}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <div className="it-table-actions">
                        <button
                          className="it-btn-edit"
                          title="Edit configuration"
                          onClick={() => handleOpenEdit(item)}
                        >
                          <MdEdit size={14} /> Edit
                        </button>
                        <button
                          className="it-btn-delete"
                          title="Delete configuration"
                          onClick={() => handleOpenDelete(item)}
                        >
                          <MdDelete size={14} /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7">
                    <div className="it-empty-state">
                      No User TopCoin configurations found matching your search.
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Modal */}
      {isEditModalOpen && (
        <div className="it-modal-overlay">
          <div className="it-modal-box">
            <div className="it-modal-header">
              <h3>Edit User TopCoin: {selectedRecord?.UserType}</h3>
              <button
                className="it-modal-close"
                onClick={() => setIsEditModalOpen(false)}
              >
                &times;
              </button>
            </div>
            <form onSubmit={handleSaveEdit}>
              <div className="it-modal-body">
                <div className="it-form-field">
                  <label>User Type</label>
                  <input
                    type="text"
                    value={formData.UserType}
                    onChange={(e) =>
                      setFormData({ ...formData, UserType: e.target.value })
                    }
                    required
                  />
                  {/* <small>Name of the role/user type in the system</small> */}
                </div>

                <div className="it-form-field">
                  <label>TopCoin Value</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.TopCoinValue}
                    onChange={(e) =>
                      setFormData({ ...formData, TopCoinValue: e.target.value })
                    }
                    required
                  />
                  {/* <small>Baseline unit multiplier (default: 1)</small> */}
                </div>

                <div className="it-form-field">
                  <label>Cash Value (£)</label>
                  <input
                    type="number"
                    step="0.0001"
                    min="0.0001"
                    value={formData.CashValue}
                    onChange={(e) =>
                      setFormData({ ...formData, CashValue: e.target.value })
                    }
                    required
                  />
                  {/* <small>Monetary cash conversion rate per 1 TopCoin (e.g. 0.0050 = £0.0050)</small> */}
                </div>

                <div className="it-form-field">
                  <label>Minimum TopCoin To Claim</label>
                  <input
                    type="number"
                    min="0"
                    placeholder="e.g. 250 or 10000"
                    value={formData.MinimumTopCoinToClaim}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        MinimumTopCoinToClaim: e.target.value,
                      })
                    }
                  />
                  {/* <small>Minimum balance threshold required before claiming cashback</small> */}
                </div>

                <div className="it-form-field">
                  <label>Notes (Optional)</label>
                  <textarea
                    rows="3"
                    placeholder="Additional details or conditions..."
                    value={formData.Notes}
                    onChange={(e) =>
                      setFormData({ ...formData, Notes: e.target.value })
                    }
                  />
                </div>
              </div>

              <div className="it-modal-footer">
                <button
                  type="button"
                  className="it-btn-cancel"
                  onClick={() => setIsEditModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="action-btn"
                  style={{ marginTop: "0px" }}
                  disabled={isSaving}
                >
                  {isSaving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Modal */}
      {isAddModalOpen && (
        <div className="it-modal-overlay">
          <div className="it-modal-box">
            <div className="it-modal-header">
              <h3>Add New User TopCoin Setting</h3>
              <button
                className="it-modal-close"
                onClick={() => setIsAddModalOpen(false)}
              >
                &times;
              </button>
            </div>
            <form onSubmit={handleSaveAdd}>
              <div className="it-modal-body">
                <div className="it-form-field">
                  <label>User Type *</label>
                  <input
                    type="text"
                    placeholder="e.g. Member, Customer, Retail Business"
                    value={formData.UserType}
                    onChange={(e) =>
                      setFormData({ ...formData, UserType: e.target.value })
                    }
                    required
                  />
                  {/* <small>Unique name of the user type</small> */}
                </div>

                <div className="it-form-field">
                  <label>TopCoin Value *</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.TopCoinValue}
                    onChange={(e) =>
                      setFormData({ ...formData, TopCoinValue: e.target.value })
                    }
                    required
                  />
                  {/* <small>Baseline unit multiplier (default: 1)</small> */}
                </div>

                <div className="it-form-field">
                  <label>Cash Value (£) *</label>
                  <input
                    type="number"
                    step="0.0001"
                    min="0.0001"
                    value={formData.CashValue}
                    onChange={(e) =>
                      setFormData({ ...formData, CashValue: e.target.value })
                    }
                    required
                  />
                  {/* <small>Monetary cash conversion rate per 1 TopCoin (default: 0.0050)</small> */}
                </div>

                <div className="it-form-field">
                  <label>Minimum TopCoin To Claim</label>
                  <input
                    type="number"
                    min="0"
                    placeholder="e.g. 10000"
                    value={formData.MinimumTopCoinToClaim}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        MinimumTopCoinToClaim: e.target.value,
                      })
                    }
                  />
                  {/* <small>Minimum balance threshold required to claim (e.g. 250 for Customer, 10000 for others)</small> */}
                </div>

                <div className="it-form-field">
                  <label>Notes (Optional)</label>
                  <textarea
                    rows="3"
                    placeholder="Optional notes or description..."
                    value={formData.Notes}
                    onChange={(e) =>
                      setFormData({ ...formData, Notes: e.target.value })
                    }
                  />
                </div>
              </div>

              <div className="it-modal-footer">
                <button
                  type="button"
                  className="it-btn-cancel"
                  onClick={() => setIsAddModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="action-btn"
                  style={{ marginTop: "0px" }}
                  disabled={isSaving}
                >
                  {isSaving ? "Creating..." : "Create Setting"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div className="it-modal-overlay">
          <div className="it-modal-box" style={{ maxWidth: "450px" }}>
            <div className="it-modal-header" style={{ background: "#fef2f2" }}>
              <h3 style={{ color: "#991b1b" }}>Confirm Deletion</h3>
              <button
                className="it-modal-close"
                onClick={() => setIsDeleteModalOpen(false)}
              >
                &times;
              </button>
            </div>
            <div className="it-modal-body">
              <p style={{ margin: "0 0 10px 0", color: "#334155", fontSize: "15px" }}>
                Are you sure you want to delete the configuration for{" "}
                <strong>"{selectedRecord?.UserType}"</strong>?
              </p>
              <p style={{ margin: 0, fontSize: "13px", color: "#64748b" }}>
                This action cannot be undone. Users with this role may default to fallback values if queried.
              </p>
            </div>
            <div className="it-modal-footer">
              <button
                type="button"
                className="it-btn-cancel"
                onClick={() => setIsDeleteModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="action-btn"
                style={{ background: "#dc2626" }}
                onClick={handleConfirmDelete}
                disabled={isSaving}
              >
                {isSaving ? "Deleting..." : "Delete Configuration"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Snackbar Notification */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={handleSnackClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackClose}
          severity={snack.severity}
          sx={{ width: "100%" }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
